import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { collegeJourneyProgressService } from '../../lib/college-journey-service';
import { supabase } from '../../lib/supabase';
import CollegeJourneyPage from './CollegeJourneyPage';
import ParentCollegeHub from './ParentCollegeHub';

const CollegeRouteHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useFamilyContext();
  const { colleges } = useCollegeContext();
  const initialCheckDone = useRef(false);
  
  useEffect(() => {
    // Only run initial onboarding check, not on every user switch
    if (!currentUser || initialCheckDone.current) return;
    
    const checkOnboardingStatus = async () => {
      console.log('CollegeRouteHandler: Checking onboarding status for user:', currentUser);
      
      // Check if user is a parent
      const isParent = currentUser?.isParent || currentUser?.type === 'parent';
      
      // Check if there's any college data (indicating family setup)
      const hasCollegeData = colleges && colleges.length > 0;
      
      let onboardingComplete = false;
      
      try {
        // Check both tables for onboarding status - student_onboarding_progress and college_journey_progress
        // First check student_onboarding_progress (primary table)
        let progress = null;
        
        const { data: studentProgress, error: studentError } = await supabase
          .from('student_onboarding_progress')
          .select('onboarding_complete')
          .eq('user_id', currentUser.id)
          .single();
        
        if (studentProgress && !studentError) {
          progress = studentProgress;
          console.log('CollegeRouteHandler: Found progress in student_onboarding_progress:', progress);
        } else {
          // Fallback to college_journey_progress table
          progress = await collegeJourneyProgressService.getProgress(currentUser.id);
          console.log('CollegeRouteHandler: Checking college_journey_progress:', progress);
        }
        
        onboardingComplete = progress?.onboarding_complete || false;
        
        console.log('CollegeRouteHandler: Database onboarding status:', onboardingComplete);
        
        // If no database record, check localStorage and migrate
        if (!progress) {
          const localOnboardingComplete = localStorage.getItem('college-onboarding-complete');
          onboardingComplete = !!localOnboardingComplete;
          
          console.log('CollegeRouteHandler: localStorage onboarding status:', onboardingComplete);
          
          // Migrate to database if localStorage has data
          if (localOnboardingComplete) {
            try {
              await collegeJourneyProgressService.markOnboardingComplete(currentUser.id);
              console.log('✅ Migrated onboarding status to database');
            } catch (error) {
              console.error('Error migrating onboarding status:', error);
            }
          }
        }
      } catch (error) {
        console.error('Error checking database onboarding status:', error);
        // Fallback to localStorage
        const localOnboardingComplete = localStorage.getItem('college-onboarding-complete');
        onboardingComplete = !!localOnboardingComplete;
        console.log('CollegeRouteHandler: Fallback to localStorage:', onboardingComplete);
      }
      
      console.log('CollegeRouteHandler: Final status - onboardingComplete:', onboardingComplete, 'isParent:', isParent, 'hasCollegeData:', hasCollegeData);
      
      // If user is a parent and no college data exists, they'll see the setup overview in the unified hub
      // No redirect needed - the ParentCollegeHub will handle showing setup overview first
      
      // If user is a student and hasn't completed onboarding, show onboarding
      if (!isParent && !onboardingComplete) {
        console.log('CollegeRouteHandler: Redirecting to onboarding');
        navigate('/college/onboarding');
        return;
      }
      
      console.log('CollegeRouteHandler: User can proceed to main hub');
      // Otherwise, user can proceed to main hub
      // No navigation needed - component will render CollegeJourneyHub
    };

    checkOnboardingStatus();
    initialCheckDone.current = true;
  }, [currentUser, colleges, navigate]);

  // Reset the check when location changes (user navigates away and back)
  useEffect(() => {
    if (location.pathname !== '/college') {
      initialCheckDone.current = false;
    }
  }, [location.pathname]);

  // Both parents and students now use the unified CollegeJourneyPage navigation
  return <CollegeJourneyPage />;
};

export default CollegeRouteHandler;